import { ThemeProvider } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useMemo } from 'react';
import { useUpdateEffect } from 'react-use';

import {
  createPortalTheme,
  mapThemeToPortalThemeOptions,
} from '@frontend-libs/portal';

import { PortalConfigurationModel } from '@portal-clients/configuration';

export interface PortalConfigurationContextType {
  config: PortalConfigurationModel;
  refetch(completed?: () => void): void;
}

export interface PortalConfigurationProviderProps {
  children: React.ReactNode;
  config: PortalConfigurationModel | undefined;
  language: string;
}

export const PortalConfigurationContext =
  createContext<PortalConfigurationContextType>(null!);

export const PortalConfigurationProvider = (
  props: PortalConfigurationProviderProps,
) => {
  const queryClient = useQueryClient();

  const refetch = useCallback<PortalConfigurationContextType['refetch']>(
    (completed) => {
      queryClient
        .invalidateQueries({ queryKey: ['portal-configuration'] })
        .finally(() => {
          completed?.();
        });
    },
    [queryClient],
  );

  useUpdateEffect(refetch, [props.language]);

  const theme = useMemo(() => {
    if (!props.config?.theme) {
      return undefined;
    }

    return createPortalTheme(mapThemeToPortalThemeOptions(props.config.theme));
  }, [props.config?.theme]);

  const children = (
    <PortalConfigurationContext.Provider
      value={{
        // To be able to support the option hook and the required hook we need
        // to be able to create the context with undefined. The hooks provide
        // the correct types.
        config: props.config!,
        refetch,
      }}
    >
      {props.children}
    </PortalConfigurationContext.Provider>
  );

  if (theme) {
    return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
  }

  return children;
};

export const usePortalConfiguration = () => {
  const context = useContext(PortalConfigurationContext);

  if (!context) {
    throw new Error(
      'usePortalConfiguration must be used within a PortalConfigurationProvider',
    );
  }

  return context;
};

export const useOptionalPortalConfiguration = ():
  | PortalConfigurationModel
  | undefined => {
  const context = useContext(PortalConfigurationContext);
  return context?.config;
};
